/**
 * Project Budget tab. Planned versus actual comes from the cost summary.
 * The earned-value panel stays off until EV-0-100-1.
 * baselinesEnabled is read from the project, never hardcoded.
 */
import React, { useState } from 'react';
import { useParams } from 'react-router-dom';

import { useWorkspaceStore } from '@/state/workspace.store';
import { useAuth } from '@/state/AuthContext';
import { BaselinePanel } from '@/features/projects/components/BaselinePanel';
import { BudgetSummaryPanel } from '@/features/projects/components/BudgetSummaryPanel';
import { ProjectBudgetAmountField } from '@/features/projects/components/ProjectBudgetAmountField';
import { useProjectContext } from '@/features/projects/layout/ProjectPageLayout';
import { projectsApi } from '@/features/projects/projects.api';
import { useWorkspaceRole } from '@/hooks/useWorkspaceRole';
import { canSeeCost } from '@/utils/access';

export const ProjectBudgetTab: React.FC = () => {
  const { projectId } = useParams<{ projectId: string }>();
  const workspaceRole = useWorkspaceStore((s) => s.workspaceRole);
  const { user } = useAuth();
  const { project, refresh, isReadOnly } = useProjectContext();
  const { role } = useWorkspaceRole(project?.workspaceId ?? null);
  const [summaryEpoch, setSummaryEpoch] = useState(0);
  const [actualDraft, setActualDraft] = useState('');
  const [actualError, setActualError] = useState<string | null>(null);

  if (!projectId) {
    return <p className="text-sm text-gray-500 p-4">Loading...</p>;
  }

  const baselinesEnabled = project?.baselinesEnabled === true;
  const isProjectManager = Boolean(user?.id && project?.projectManagerId === user.id);
  const isAdmin = role === 'OWNER' || role === 'ADMIN';
  const canRecordActual = (isAdmin || isProjectManager) && canSeeCost(null, user?.platformRole ?? user?.role) && !isReadOnly;

  const saveActual = async () => {
    if (!project) return;
    const parsed = Number(actualDraft);
    if (actualDraft.trim() === '' || !Number.isFinite(parsed) || parsed < 0) {
      setActualError('Enter an actual cost.');
      return;
    }
    setActualError(null);
    await projectsApi.setActualCost(project.id, parsed, project.workspaceId);
    await refresh();
    setSummaryEpoch((n) => n + 1);
  };

  return (
    <div className="space-y-6 p-4" data-testid="project-budget-tab">
      <div>
        <h2 className="text-lg font-semibold text-slate-900">Budget</h2>
        <p className="mt-1 text-sm text-slate-500">
          Planned versus actual cost for this project.
        </p>
      </div>

      <ProjectBudgetAmountField
        onSaved={() => {
          setSummaryEpoch((n) => n + 1);
        }}
      />

      {canRecordActual && project ? (
        <div className="space-y-2">
          <label className="block text-sm text-slate-700" htmlFor="actual-cost">
            Actual cost
            <input
              id="actual-cost"
              aria-label="Actual cost"
              className="mt-1 w-full max-w-xs rounded border px-2 py-1"
              inputMode="decimal"
              value={actualDraft}
              onChange={(e) => setActualDraft(e.target.value)}
            />
          </label>
          {actualError ? <p className="text-sm text-red-700">{actualError}</p> : null}
          <button type="button" className="text-sm" onClick={() => void saveActual()}>
            Save actual cost
          </button>
        </div>
      ) : null}

      <BudgetSummaryPanel key={summaryEpoch} projectId={projectId} />

      <BaselinePanel
        readOnly={isReadOnly}
        projectId={projectId}
        baselinesEnabled={baselinesEnabled}
        workspaceRole={workspaceRole ?? undefined}
      />
    </div>
  );
};

export default ProjectBudgetTab;
